import { useMenuOuvert } from '../partage/useMenuOuvert';
import { PROFONDEUR, RECENTRER, RECENTRER_TITRE } from '../partage/vocabulaire';
import './SelecteurProfondeur.css';

/**
 * Le sélecteur de profondeur (ticket 13 de retours-testeurs), sur la ligne des angles d'un projet,
 * en Carte comme en Hiérarchie (chantier `tete-du-projet`, modification 01, retour d'Igor du
 * 2026-10-20 : « profondeur est un simple bouton, le choix est dedans », maquette du carnet
 * `projet-ouvert.html`, `.btn` « Profondeur 1 »). Un seul bouton, « Profondeur 1 », le niveau
 * choisi dedans ; il ouvre un petit menu : un rond par niveau, puis, en Carte, « Recentrer »
 * (décision 4 d'Igor). Il se replie sur un choix, Échap ou un clic à côté (`useMenuOuvert`).
 *
 * Les repères du tuto restent : `profondeur` sur le groupe, `profondeur-losange` sur chaque
 * niveau (visibles une fois le menu ouvert). Il ne décide rien : les règles (socle, molette
 * par-dessus, reclic qui referme tout) sont dans `profondeur.ts`.
 */
export function SelecteurProfondeur({
  niveaux,
  actuelle,
  onChoisir,
  onRecentrer = null,
}: {
  niveaux: number;
  actuelle: number;
  onChoisir: (profondeur: number) => void;
  /** « Recentrer », en Carte seulement ; `null` ailleurs. */
  onRecentrer?: (() => void) | null;
}) {
  const menu = useMenuOuvert();
  const losanges = Array.from({ length: niveaux }, (_, i) => i + 1);
  const titre = (n: number) =>
    n === actuelle ? `Profondeur ${n} · Reclique pour tout refermer jusqu’ici` : `Profondeur ${n}`;

  return (
    <div className="profondeur" role="group" aria-label="profondeur" data-tuto="profondeur" ref={menu.ref}>
      <button
        type="button"
        className="profondeur-bouton"
        aria-haspopup="true"
        aria-expanded={menu.ouvert}
        onClick={() => menu.setOuvert(!menu.ouvert)}
      >
        {PROFONDEUR} <b>{actuelle}</b>
      </button>

      {menu.ouvert && (
        <div className="profondeur-menu">
          <div className="profondeur-niveaux">
            {losanges.map((n) => (
              <button
                key={n}
                type="button"
                className={`profondeur-niveau${n === actuelle ? ' est-choisi' : ''}`}
                onClick={() => {
                  menu.setOuvert(false);
                  onChoisir(n);
                }}
                data-tuto="profondeur-losange"
                aria-pressed={n === actuelle}
                aria-label={`profondeur ${n}`}
                title={titre(n)}
              >
                {n}
              </button>
            ))}
          </div>
          {onRecentrer && (
            <button
              type="button"
              className="profondeur-recentrer"
              title={RECENTRER_TITRE}
              onClick={() => {
                menu.setOuvert(false);
                onRecentrer();
              }}
            >
              <span aria-hidden="true">⌖</span> {RECENTRER}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
